/**
 * An app's starting rows, written into its owner-managed collections on its FIRST publish.
 *
 * PURE: it reads the source with the gate's own extractor (`listStartingRows`) and writes
 * through whatever client it is handed. It lived in saddleHost.ts, which imports `textAsset`
 * and through it Cognito and React Native, so the web builder could not reach it — and never
 * seeded. Every web-built app with `S.list('photos', [...])` painted the model's rows, then
 * the live read answered `{items: []}` and the gallery switched to "Photos coming soon."
 * (F1, the web deep-dive of 2026-09-30). One module now, for the phone and the browser.
 */
import { listStartingRows } from './gate';
import { resolveDateToken } from './dateToken';

/** What seeding needs of a backend: read one row of a collection, and add one. */
export interface SeedClient {
  listRecords: (entity: string, opts?: { limit?: number }) => Promise<{ items: unknown[] }>;
  createRecord: (entity: string, data: Record<string, unknown>) => Promise<unknown>;
}

/**
 * Turns a relative token in a starting row into a real date, at publish time.
 *
 * Starting rows have to be literals so the publisher can read them, and a literal cannot
 * call new Date(). Hardcoding "12 September" instead means a supper club built in November
 * ships with dates that have already passed — which is the "seeded dates must be computed
 * from new Date()" rule the store seeds already follow, reappearing in a place where the
 * usual answer is impossible.
 *
 * So a row writes '+3d 18:30' and this resolves it against the moment of publish. Anything
 * that is not a token is passed through untouched — which is what a date the PERSON gave
 * needs: a wedding on '2027-06-11 18:00' is that evening, whenever the app is published.
 */
export function resolveRelativeDates(row: Record<string, unknown>, now = new Date()): Record<string, unknown> {
  const out: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(row)) {
    // The resolution itself lives beside the grammar (`resolveDateToken`), because the gate
    // now reads a row's dates too and must land them exactly where this does.
    const when = typeof value === 'string' ? resolveDateToken(value, now) : null;
    out[key] = when ? written(value as string, when) : value;
  }

  return out;
}

const pad = (n: number) => String(n).padStart(2, '0');

/**
 * The resolved token as the CLOCK it names, with no zone: '2026-10-19T06:30'.
 *
 * It was `toISOString()`, an instant in UTC. '+1w mon 06:30' published from a Pacific browser
 * became '2026-10-19T13:30:00.000Z': a UK visitor saw the café's 6:30 open at 2:30 PM, and the
 * page's time bars, which read the hour off the text, drew it from 13:30 for everybody
 * (caf-shift-board, 2026-10-13). A written time is the clock where the event happens — the rule
 * the person's own dates already follow — and a string with no zone is read as local time by
 * every browser, so every reader sees 06:30.
 *
 * A day with no clock is that day from midnight (written with the T00:00, because a bare
 * 'YYYY-MM-DD' is read as UTC midnight by `new Date`, the day before west of Greenwich). '+3h'
 * stays an instant: it means three hours from now, wherever that is.
 */
function written(token: string, when: Date): string {
  if (/^\+\d{1,3}h/i.test(token.trim())) return when.toISOString();
  const day = `${when.getFullYear()}-${pad(when.getMonth() + 1)}-${pad(when.getDate())}`;
  return /\d:\d{2}$/.test(token.trim()) ? `${day}T${pad(when.getHours())}:${pad(when.getMinutes())}` : `${day}T00:00`;
}

/**
 * Writes an app's starting rows into its owner-managed collections, once.
 *
 * Only into an EMPTY collection. A republish must never duplicate the owner's content, and
 * must never overwrite an edit they made since — by the second publish those rows are the
 * owner's data, not the model's suggestion.
 *
 * Best effort throughout. The app is already published and working by this point; failing
 * the whole hosting step because a seed row was rejected would take away a link that
 * exists, to fix a screen that is merely empty.
 */
export async function seedOwnerContent(
  appId: string,
  source: string,
  client: SeedClient,
): Promise<{ seeded: Record<string, number> }> {
  const seeded: Record<string, number> = {};
  let starting: Record<string, Record<string, unknown>[]> = {};
  try {
    starting = listStartingRows(source);
  } catch {
    return { seeded };
  }

  for (const [entity, rows] of Object.entries(starting)) {
    if (!rows.length) continue;
    try {
      const existing = await client.listRecords(entity, { limit: 1 });
      if ((existing?.items?.length ?? 0) > 0) continue; // the owner's content already
      let written = 0;
      for (const row of rows) {
        try {
          await client.createRecord(entity, resolveRelativeDates(row));
          written += 1;
        } catch {
          /* one bad row must not stop the rest */
        }
      }
      if (written) seeded[entity] = written;
    } catch {
      /* cannot read the collection — leave it alone rather than risk duplicating */
    }
  }

  return { seeded };
}

/** A name a photo could go by in `S.media(name)`: a file name — never an address or a path. */
const PHOTO_NAME = /^[^/\\:?#]+\.(jpe?g|png|gif|webp|heic|heif|avif)$/i;

/**
 * The photo names an app's starting rows hand to `S.media` — a gallery written as
 * `S.media(p.imageUrl)` over `S.list('photos', [{ imageUrl: 'porch.jpg' }, …])`.
 *
 * The web build's "your page has spots for photos it does not have" read only literal
 * `S.media('x')` calls, so the hero was named and the gallery's empty tiles were not (review of
 * lane/build-publish, 2026-09-30). Read with the seeder's own extractor, so these are exactly
 * the rows the first publish writes. Only when the page passes S.media something other than a
 * literal: a row value no S.media call can reach is a file name, not a spot on the page.
 */
export function startingRowPhotoNames(source: string): string[] {
  if (!/S\.media\(\s*[^'"`\s)]/.test(source)) return [];
  let starting: Record<string, Record<string, unknown>[]> = {};
  try {
    starting = listStartingRows(source);
  } catch {
    return [];
  }
  const names = new Set<string>();
  const take = (v: unknown): void => {
    if (typeof v === 'string' && PHOTO_NAME.test(v.trim())) names.add(v.trim());
  };
  for (const rows of Object.values(starting)) {
    for (const row of rows) {
      for (const value of Object.values(row)) {
        if (Array.isArray(value)) value.forEach(take);
        else take(value);
      }
    }
  }
  return [...names];
}
